import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, {
    setStoredToken,
    clearStoredToken,
    getStoredToken,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [initializing, setInitializing] = useState(true);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (e) {
            setUser(null);
            clearStoredToken();
            return null;
        }
    }, []);

    useEffect(() => {
        (async () => {
            const token = getStoredToken();
            if (token) {
                await fetchMe();
            }
            setInitializing(false);
        })();
    }, [fetchMe]);

    const login = async ({ email, password, rememberMe }) => {
        const { data } = await api.post("/auth/login", {
            email,
            password,
            remember_me: !!rememberMe,
        });
        setStoredToken(data.access_token, !!rememberMe);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_e) {
            // ignore
        }
        clearStoredToken();
        setUser(null);
    };

    const updateUser = (patch) => {
        setUser((prev) => (prev ? { ...prev, ...patch } : prev));
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                initializing,
                isAuthenticated: !!user,
                login,
                logout,
                fetchMe,
                updateUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
